// [PURPOSE]: Warlock-specific panel — Pact Boon + Eldritch Invocation picker.
// [WHY]: Warlock players previously had no UI for Pact Boon (lvl 3) or Invocations
//        (2/5/7/9/12/15/18). Only auto-adds features from SRD were visible.
// [INTEGRATION]: Rendered conditionally inside CharacterBasicsPanel or FeatsPanel.
//        Reads playerStats.classes[warlockIdx]; writes pactBoon + invocations[].
// [SCOPE]: Records choices + shows descriptions. Invocations with an `effects`
//        array (e.g., Armor of Shadows) register into effectRegistry on mount.

import React, { useMemo, useState, useEffect } from 'react';
import { Check, X, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { useCharacter } from '../contexts/CharacterContext';
import { useSettings } from '../contexts/SettingsContext';
import { effectRegistry } from '../services/effectRegistry';
import { effectCalculator } from '../services/effectCalculator';
import {
  PACT_BOONS,
  ELDRITCH_INVOCATIONS,
  invocationsKnownAtLevel,
  eligibleInvocations,
} from '../data/warlockPact';

/**
 * Find the Warlock class entry in a character's classes array.
 * Case-insensitive match on className.
 */
function findWarlockEntry(classes) {
  if (!Array.isArray(classes)) return { entry: null, index: -1 };
  const index = classes.findIndex(c => String(c.className || '').toLowerCase() === 'warlock');
  return { entry: index >= 0 ? classes[index] : null, index };
}

const WarlockPactPanel = () => {
  const { playerStats, setPlayerStats, logActivity, knownSpells } = useCharacter();
  const { settings } = useSettings();
  const [expanded, setExpanded] = useState(true);
  const [showBoonPicker, setShowBoonPicker] = useState(false);
  const [showInvocationPicker, setShowInvocationPicker] = useState(false);

  const { entry: warlockEntry, index: warlockIdx } = findWarlockEntry(playerStats?.classes);
  const warlockLevel = warlockEntry?.level || 0;

  // Which content sources are enabled (PHB + whatever the user toggled on in Sources).
  // Fall back to phb-only if settings are missing.
  const enabledSources = useMemo(() => {
    const fromSettings = settings?.enabledSources;
    if (Array.isArray(fromSettings) && fromSettings.length > 0) return fromSettings;
    // Best-effort: include common sources when the user has premium / content-workshop
    return ['phb', 'xanathars', 'tashas'];
  }, [settings]);

  const currentBoonId = warlockEntry?.pactBoon || null;
  const currentInvocations = Array.isArray(warlockEntry?.invocations)
    ? warlockEntry.invocations
    : [];

  const maxInvocations = invocationsKnownAtLevel(warlockLevel);
  const eligible = useMemo(
    () => eligibleInvocations(warlockLevel, currentBoonId, enabledSources, knownSpells),
    [warlockLevel, currentBoonId, enabledSources, knownSpells]
  );

  // [FIX APR 2026 — Phase 4]: Register mechanical effects for active invocations.
  // Same pattern as FeatsPanel lines 99-117. Runs before the early-return guard so
  // effects are also cleaned up when the character has no Warlock levels (returns []).
  useEffect(() => {
    effectRegistry.removeByPrefix('invocation:');
    currentInvocations.forEach(invId => {
      const inv = ELDRITCH_INVOCATIONS.find(i => i.id === invId);
      if (inv?.effects && Array.isArray(inv.effects)) {
        try {
          effectRegistry.register(`invocation:${invId}`, inv.effects, playerStats);
        } catch (err) {
          console.warn(`[WarlockPactPanel] Effect registration failed for ${inv.name}:`, err);
        }
      }
    });
    effectCalculator.clearCache();
    return () => {
      effectRegistry.removeByPrefix('invocation:');
      effectCalculator.clearCache();
    };
  }, [playerStats.classes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only render if the character has at least one level of Warlock.
  if (!warlockEntry || warlockIdx < 0) return null;

  const updateWarlock = (patch) => {
    setPlayerStats(prev => {
      const classes = Array.isArray(prev.classes) ? [...prev.classes] : [];
      if (warlockIdx < 0 || !classes[warlockIdx]) return prev;
      classes[warlockIdx] = { ...classes[warlockIdx], ...patch };
      return { ...prev, classes };
    });
  };

  const pickBoon = (boonId) => {
    updateWarlock({ pactBoon: boonId });
    if (logActivity) {
      logActivity(`Took ${PACT_BOONS[boonId].name}`, 'character', { pactBoon: boonId });
    }
    setShowBoonPicker(false);
  };

  const toggleInvocation = (invId) => {
    const has = currentInvocations.includes(invId);
    let nextList;
    if (has) {
      nextList = currentInvocations.filter(id => id !== invId);
    } else {
      if (currentInvocations.length >= maxInvocations) return; // capped
      nextList = [...currentInvocations, invId];
    }
    updateWarlock({ invocations: nextList });
    const inv = ELDRITCH_INVOCATIONS.find(i => i.id === invId);
    if (logActivity && inv) {
      logActivity(`${has ? 'Forgot' : 'Learned'} ${inv.name}`, 'character', { invocation: invId });
    }
  };

  const canChooseBoon = warlockLevel >= 3;
  const canChooseInvocations = warlockLevel >= 2;

  return (
    <div className="bg-gray-800 rounded-lg p-4 border border-indigo-700 shadow-lg mb-4">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <Sparkles size={20} className="text-indigo-400" />
          <h3 className="text-lg font-bold text-indigo-300">Warlock Pact</h3>
          <span className="text-xs text-gray-400">(Warlock {warlockLevel})</span>
        </div>
        {expanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
      </button>

      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Pact Boon section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-indigo-200">Pact Boon</h4>
              {canChooseBoon && (
                <button
                  type="button"
                  onClick={() => setShowBoonPicker(v => !v)}
                  className="text-xs px-2 py-1 rounded bg-indigo-700 hover:bg-indigo-600 text-white"
                >
                  {currentBoonId ? 'Change' : 'Choose'}
                </button>
              )}
            </div>
            {!canChooseBoon ? (
              <div className="text-xs text-gray-500">Unlocks at Warlock level 3.</div>
            ) : currentBoonId ? (
              <div className="bg-gray-900/60 rounded p-3 border border-indigo-800/50">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{PACT_BOONS[currentBoonId].icon}</span>
                  <span className="font-bold text-white">{PACT_BOONS[currentBoonId].name}</span>
                </div>
                <div className="text-xs text-gray-300 mt-1">{PACT_BOONS[currentBoonId].description}</div>
              </div>
            ) : (
              <div className="text-xs text-amber-300">No Pact Boon chosen yet — pick one.</div>
            )}

            {showBoonPicker && canChooseBoon && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.values(PACT_BOONS)
                  .filter(b => enabledSources.includes(b.source))
                  .map(boon => {
                    const isCurrent = boon.id === currentBoonId;
                    return (
                      <button
                        key={boon.id}
                        type="button"
                        onClick={() => pickBoon(boon.id)}
                        className={`text-left p-3 rounded border transition-colors ${
                          isCurrent
                            ? 'border-indigo-400 bg-indigo-900/40'
                            : 'border-gray-700 bg-gray-900/40 hover:border-indigo-600 hover:bg-gray-900/70'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{boon.icon}</span>
                          <span className="font-bold text-white">{boon.name}</span>
                          {isCurrent && <Check size={14} className="text-green-400" />}
                          <span className="ml-auto text-[10px] uppercase tracking-wider text-gray-500">{boon.source}</span>
                        </div>
                        <div className="text-xs text-gray-300">{boon.summary}</div>
                      </button>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Invocations section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-indigo-200">
                Eldritch Invocations
                <span className="ml-2 text-xs text-gray-400">
                  ({currentInvocations.length} / {maxInvocations})
                </span>
              </h4>
              {canChooseInvocations && (
                <button
                  type="button"
                  onClick={() => setShowInvocationPicker(v => !v)}
                  className="text-xs px-2 py-1 rounded bg-indigo-700 hover:bg-indigo-600 text-white"
                >
                  {showInvocationPicker ? 'Done' : 'Manage'}
                </button>
              )}
            </div>
            {!canChooseInvocations ? (
              <div className="text-xs text-gray-500">Unlocks at Warlock level 2.</div>
            ) : currentInvocations.length === 0 ? (
              <div className="text-xs text-amber-300">
                You can learn {maxInvocations} invocation{maxInvocations !== 1 ? 's' : ''}.
              </div>
            ) : (
              <div className="space-y-2">
                {currentInvocations.map(id => {
                  const inv = ELDRITCH_INVOCATIONS.find(i => i.id === id);
                  if (!inv) {
                    return (
                      <div key={id} className="text-xs text-gray-500 italic">
                        Unknown invocation: {id}
                      </div>
                    );
                  }
                  return (
                    <div key={id} className="bg-gray-900/60 rounded p-2 border border-indigo-800/40">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-white text-sm">{inv.name}</span>
                        <span className="text-[10px] uppercase text-gray-500">{inv.source}</span>
                      </div>
                      <div className="text-xs text-gray-300 mt-1">{inv.description}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {showInvocationPicker && canChooseInvocations && (
              <div className="mt-3 border-t border-gray-700 pt-3 space-y-2">
                <div className="text-xs text-gray-400">
                  Click to toggle. Max {maxInvocations} at your current level.
                </div>
                {eligible.length === 0 ? (
                  <div className="text-xs text-amber-300">No invocations available with current settings.</div>
                ) : (
                  eligible.map(inv => {
                    const picked = currentInvocations.includes(inv.id);
                    const canTakeMore = currentInvocations.length < maxInvocations;
                    const disabled = !picked && !canTakeMore;
                    return (
                      <button
                        key={inv.id}
                        type="button"
                        onClick={() => toggleInvocation(inv.id)}
                        disabled={disabled}
                        className={`w-full text-left p-2 rounded border transition-colors ${
                          picked
                            ? 'border-green-500 bg-green-900/20'
                            : disabled
                              ? 'border-gray-800 bg-gray-900/30 opacity-50 cursor-not-allowed'
                              : 'border-gray-700 bg-gray-900/40 hover:border-indigo-600'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {picked ? (
                            <Check size={14} className="text-green-400 flex-shrink-0" />
                          ) : (
                            <span className="w-3.5 h-3.5 border border-gray-600 rounded-sm flex-shrink-0" />
                          )}
                          <span className="font-semibold text-white text-sm">{inv.name}</span>
                          <span className="text-[10px] uppercase text-gray-500 ml-auto">{inv.source}</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1 ml-5">{inv.description}</div>
                        {inv.requires && (
                          <div className="text-[10px] text-indigo-300 mt-1 ml-5">
                            Requires: {inv.requires.pactBoon ? `Pact of the ${inv.requires.pactBoon.charAt(0).toUpperCase() + inv.requires.pactBoon.slice(1)}` : ''}
                            {inv.requires.spell ? `${inv.requires.pactBoon ? ' • ' : ''}${inv.requires.spell}` : ''}
                          </div>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(WarlockPactPanel);
